import React, { useEffect, useState } from 'react';
import { collection, getDocs, updateDoc, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { emailService } from '../../services/emailService';

export default function OrdersList() {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const load = async () => {
      const snap = await getDocs(collection(db, 'orders'));
      setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    load();
  }, []);

  const fetchOrderDetails = async (orderId) => {
    setLoadingDetails(true);
    try {
      const orderDoc = await getDoc(doc(db, 'orders', orderId));
      if (orderDoc.exists()) {
        const orderData = { id: orderDoc.id, ...orderDoc.data() };
        
        // Fetch product details for each item
        const itemsWithProducts = await Promise.all(
          orderData.items.map(async (item) => {
            try {
              const productDoc = await getDoc(doc(db, 'products', item.productId));
              return {
                ...item,
                productData: productDoc.exists() ? { id: productDoc.id, ...productDoc.data() } : null
              };
            } catch (error) {
              console.error('Error fetching product:', error);
              return { ...item, productData: null };
            }
          })
        );
        
        setOrderDetails({ ...orderData, items: itemsWithProducts });
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    fetchOrderDetails(order.id);
  };

  const handleDeleteOrder = async (orderId) => {
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'orders', orderId));
      setOrders(prev => prev.filter(order => order.id !== orderId));
      setDeleteConfirm(null);
      console.log('Order deleted successfully:', orderId);
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Failed to delete order. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = (order) => {
    setDeleteConfirm({
      id: order.id,
      title: order.items?.[0]?.title || 'Unknown Product',
      status: order.status || 'cart'
    });
  };

  const handleStatusChange = async (orderId, newStatus, order) => {
    try {
      // Update order status in Firestore
      await updateDoc(doc(db, 'orders', orderId), { 
        status: newStatus,
        updatedAt: new Date()
      });
      
      // Update local state
      setOrders(prev => prev.map(x => x.id === orderId ? {...x, status: newStatus} : x));
      
      // Update orderDetails if it's the same order
      if (orderDetails && orderDetails.id === orderId) {
        setOrderDetails(prev => ({ ...prev, status: newStatus }));
      }
      
      // Send email notification based on status
      if (order?.shipping?.contact?.email && order?.items) {
        const customerName = `${order.shipping.firstName || ''} ${order.shipping.lastName || ''}`.trim() || 'Valued Customer';
        const totalAmount = order.items.reduce((sum, item) => sum + ((item.price || 0) * (item.qty || 1)), 0);
        
        try {
          switch (newStatus) {
            case 'processing':
              await emailService.sendOrderProcessingEmail(
                order.shipping.contact.email,
                orderId,
                customerName,
                order.items,
                totalAmount
              );
              console.log('📧 Processing email sent for order:', orderId);
              break;
              
            case 'shipped':
              await emailService.sendOrderShippedEmail(
                order.shipping.contact.email,
                orderId,
                customerName,
                order.items,
                totalAmount
                // Note: trackingNumber can be added later if needed
              );
              console.log('📦 Shipped email sent for order:', orderId);
              break;
              
            case 'delivered':
              await emailService.sendOrderDeliveredEmail(
                order.shipping.contact.email,
                orderId,
                customerName,
                order.items,
                totalAmount
              );
              console.log('✅ Delivered email sent for order:', orderId);
              break;
              
            default:
              console.log('No email notification for status:', newStatus);
          }
        } catch (emailError) {
          console.error('Email notification failed:', emailError);
          // Don't fail the status update if email fails
        }
      } else {
        console.log('No email sent - missing contact email or items for order:', orderId);
      }
      
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status. Please try again.');
    }
  };

  return (
    <div className="product-list-container">
      <h2>Orders</h2>
      <div className="products-table-container">
        <table className="products-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Status</th>
              <th>Items</th>
              <th>Images</th>
              <th>Contact</th>
              <th>Shipping Address</th>
              <th>Payment</th>
              <th>Update</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(o => (
              <tr key={o.id}>
                <td>{o.id}</td>
                <td>{o.status || 'cart'}</td>
                <td>{o.items ? o.items.length : 0}</td>
                <td>
                  {(o.items?.[0]?.photos || []).slice(0,3).map((p,i)=> (
                    <img key={i} src={p.url} alt="thumb" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, marginRight: 4 }} />
                  ))}
                </td>
                <td>
                  <div>{o.shipping?.contact?.email || '-'}</div>
                  <div>{o.shipping?.contact?.phone || ''}</div>
                </td>
                <td>
                  {o.shipping ? (
                    <div>
                      <div>{o.shipping.firstName} {o.shipping.lastName}</div>
                      <div>{o.shipping.address1}{o.shipping.address2 ? ', ' + o.shipping.address2 : ''}</div>
                      <div>{o.shipping.city}, {o.shipping.government}</div>
                      <div>{o.shipping.region} {o.shipping.postalCode || ''}</div>
                    </div>
                  ) : '-'}
                </td>
                <td>{o.payment ? o.payment.method : '-'}</td>
                <td>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button 
                      onClick={() => handleViewOrder(o)}
                      className="view-order-btn"
                      style={{ 
                        padding: '4px 8px', 
                        fontSize: '12px', 
                        backgroundColor: '#007bff', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      View
                    </button>
                    <button 
                      onClick={() => confirmDelete(o)}
                      className="delete-order-btn"
                      style={{ 
                        padding: '4px 8px', 
                        fontSize: '12px', 
                        backgroundColor: '#dc3545', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                    <select value={o.status || 'cart'} onChange={(e) => handleStatusChange(o.id, e.target.value, o)}>
                      <option value="cart">Cart</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                    </select>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed-preview-modal" onClick={() => { setSelectedOrder(null); setOrderDetails(null); }}>
          <div className="fixed-preview-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh' }}>
            <button 
              className="fixed-preview-close" 
              onClick={() => { setSelectedOrder(null); setOrderDetails(null); }} 
              aria-label="Close"
            >
              ×
            </button>
            
            <h3>Order Details: {selectedOrder.id}</h3>
            
            {loadingDetails ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>Loading order details...</div>
            ) : orderDetails ? (
              <div style={{ padding: '1rem', maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Order Status */}
                <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <h4>Order Status</h4>
                  <select 
                    value={orderDetails.status || 'cart'} 
                    onChange={(e) => handleStatusChange(orderDetails.id, e.target.value, orderDetails)}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  >
                    <option value="cart">Cart</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                  </select>
                </div>

                {/* User Information */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4>User Information</h4>
                  <p><strong>User ID:</strong> {orderDetails.userId}</p>
                  <p><strong>Email:</strong> {orderDetails.userEmail}</p>
                </div>

                {/* Contact Information */}
                {orderDetails.shipping?.contact && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4>Contact Information</h4>
                    <p><strong>Email:</strong> {orderDetails.shipping.contact.email}</p>
                    <p><strong>Phone:</strong> {orderDetails.shipping.contact.phone}</p>
                  </div>
                )}

                {/* Shipping Address */}
                {orderDetails.shipping && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4>Shipping Address</h4>
                    <p><strong>Name:</strong> {orderDetails.shipping.firstName} {orderDetails.shipping.lastName}</p>
                    <p><strong>Address:</strong> {orderDetails.shipping.address1}</p>
                    {orderDetails.shipping.address2 && <p><strong>Address 2:</strong> {orderDetails.shipping.address2}</p>}
                    <p><strong>City:</strong> {orderDetails.shipping.city}</p>
                    <p><strong>Government:</strong> {orderDetails.shipping.government}</p>
                    <p><strong>Region:</strong> {orderDetails.shipping.region}</p>
                    {orderDetails.shipping.postalCode && <p><strong>Postal Code:</strong> {orderDetails.shipping.postalCode}</p>}
                    {orderDetails.shipping.phone && <p><strong>Phone:</strong> {orderDetails.shipping.phone}</p>}
                  </div>
                )}

                {/* Billing Address */}
                {orderDetails.shipping?.billing && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4>Billing Address</h4>
                    {orderDetails.shipping.billing.same ? (
                      <p>Same as shipping address</p>
                    ) : (
                      <>
                        <p><strong>Name:</strong> {orderDetails.shipping.billing.firstName} {orderDetails.shipping.billing.lastName}</p>
                        <p><strong>Address:</strong> {orderDetails.shipping.billing.address1}</p>
                        {orderDetails.shipping.billing.address2 && <p><strong>Address 2:</strong> {orderDetails.shipping.billing.address2}</p>}
                        <p><strong>City:</strong> {orderDetails.shipping.billing.city}</p>
                        <p><strong>Government:</strong> {orderDetails.shipping.billing.government}</p>
                        <p><strong>Region:</strong> {orderDetails.shipping.billing.region}</p>
                        {orderDetails.shipping.billing.postalCode && <p><strong>Postal Code:</strong> {orderDetails.shipping.billing.postalCode}</p>}
                      </>
                    )}
                  </div>
                )}

                {/* Payment Method */}
                {orderDetails.payment && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4>Payment Method</h4>
                    <p><strong>Method:</strong> {orderDetails.payment.method}</p>
                  </div>
                )}

                {/* Order Items */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4>Order Items</h4>
                  {orderDetails.items.map((item, idx) => (
                    <div key={idx} style={{ border: '1px solid #eee', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                        <div style={{ flex: 1 }}>
                          <h5 style={{ margin: '0 0 0.5rem 0' }}>{item.title}</h5>
                          <p><strong>Quantity:</strong> {item.qty}</p>
                          <p><strong>Unit Price:</strong> ${item.price?.toFixed(2)}</p>
                          <p><strong>Total:</strong> ${((item.price || 0) * (item.qty || 1)).toFixed(2)}</p>
                          
                          {/* Product Category */}
                          {item.productData?.category && (
                            <p><strong>Category:</strong> {item.productData.category}</p>
                          )}
                          
                          {/* Product Description */}
                          {item.productData?.description && (
                            <p><strong>Description:</strong> {item.productData.description}</p>
                          )}
                        </div>
                        
                        {/* Product Image */}
                        {item.productData?.images?.[0] && (
                          <div style={{ marginLeft: '1rem' }}>
                            <img 
                              src={item.productData.images[0]} 
                              alt={item.title}
                              style={{ 
                                width: '120px', 
                                height: '120px', 
                                objectFit: 'cover', 
                                borderRadius: '8px',
                                border: '1px solid #ddd'
                              }} 
                            />
                          </div>
                        )}
                      </div>

                      {/* Selected Options */}
                      {item.options && Object.keys(item.options).length > 0 && item.productData?.options && (
                        <div style={{ marginBottom: '1rem' }}>
                          <h6 style={{ margin: '0 0 0.5rem 0' }}>Selected Options:</h6>
                          <ul style={{ listStyle: 'disc', marginLeft: '1.5rem', margin: 0, paddingLeft: '1.5rem' }}>
                            {Object.entries(item.options).map(([optIdx, choiceIdx]) => {
                              const option = item.productData.options[parseInt(optIdx)];
                              const choice = option?.choices?.[choiceIdx];
                              const price = option?.prices?.[choiceIdx];
                              return (
                                <li key={optIdx}>
                                  <strong>{option?.name}:</strong> {choice}
                                  {price && price > 0 && <span> (+${price.toFixed(2)})</span>}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {/* Uploaded Photos (for Photobox products) */}
                      {item.photos && item.photos.length > 0 && (
                        <div>
                          <h6 style={{ margin: '0 0 0.5rem 0' }}>Uploaded Photos ({item.photos.length}):</h6>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            {item.photos.map((photo, pIdx) => (
                              <div key={pIdx} style={{ position: 'relative' }}>
                                <img 
                                  src={photo.url} 
                                  alt={`Uploaded photo ${pIdx + 1}`} 
                                  style={{ 
                                    width: '100px', 
                                    height: '100px', 
                                    objectFit: 'cover', 
                                    borderRadius: '4px',
                                    border: '1px solid #ddd',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => window.open(photo.url, '_blank')}
                                />
                                <div style={{ 
                                  position: 'absolute', 
                                  bottom: '2px', 
                                  right: '2px', 
                                  backgroundColor: 'rgba(0,0,0,0.7)', 
                                  color: 'white', 
                                  padding: '2px 4px', 
                                  borderRadius: '2px', 
                                  fontSize: '10px' 
                                }}>
                                  {pIdx + 1}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Order Summary */}
                <div style={{ borderTop: '2px solid #007bff', paddingTop: '1rem' }}>
                  <h4>Order Summary</h4>
                  <p><strong>Total Items:</strong> {orderDetails.items.reduce((sum, item) => sum + (item.qty || 1), 0)}</p>
                  <p><strong>Subtotal:</strong> ${orderDetails.items.reduce((sum, item) => sum + ((item.price || 0) * (item.qty || 1)), 0).toFixed(2)}</p>
                  <p><strong>Order Date:</strong> {orderDetails.createdAt?.toDate?.()?.toLocaleString() || 'N/A'}</p>
                  {orderDetails.updatedAt && (
                    <p><strong>Last Updated:</strong> {orderDetails.updatedAt?.toDate?.()?.toLocaleString() || 'N/A'}</p>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center' }}>Failed to load order details</div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed-preview-modal" onClick={() => setDeleteConfirm(null)}>
          <div className="fixed-preview-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <button 
              className="fixed-preview-close" 
              onClick={() => setDeleteConfirm(null)} 
              aria-label="Close"
            >
              ×
            </button>
            
            <h3 style={{ color: '#dc3545', marginBottom: '1rem' }}>⚠️ Delete Order</h3>
            
            <div style={{ padding: '1rem' }}>
              <p style={{ marginBottom: '1rem' }}>
                Are you sure you want to delete this order? This action cannot be undone.
              </p>
              
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '1rem', 
                borderRadius: '8px', 
                marginBottom: '1.5rem',
                border: '1px solid #dee2e6'
              }}>
                <p style={{ margin: '0 0 0.5rem 0' }}><strong>Order ID:</strong> {deleteConfirm.id}</p>
                <p style={{ margin: '0 0 0.5rem 0' }}><strong>Product:</strong> {deleteConfirm.title}</p>
                <p style={{ margin: '0' }}><strong>Status:</strong> {deleteConfirm.status}</p>
              </div>
              
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button 
                  onClick={() => setDeleteConfirm(null)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button 
                  onClick={() => handleDeleteOrder(deleteConfirm.id)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    opacity: deleting ? 0.6 : 1
                  }}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


